import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../../utils/apiClient';
import {
  ShoppingCart, Clock, Calendar, Package, TrendingUp, Users,
  Menu, FileText, Settings, ChevronRight, Bell, Activity,
  DollarSign, BarChart3, AlertCircle, CheckCircle, Home,
  Utensils, Coffee, Pizza, Soup, Receipt, CreditCard,
  MessageSquare, Store, Gift, Database, ClipboardList,
  ChefHat, Truck, UserCheck, DollarSign as Money,
  Calculator, CalendarDays, History, LogOut, Lock
} from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import PageContainer from '../layout/PageContainer';
import { useOperationMode } from '../../hooks/useOperationMode';
import { useAuth } from '../../contexts/AuthContext';

interface QuickStat {
  label: string;
  value: string | number;
  change?: string;
  icon: React.ReactNode;
  color: string;
}

interface MenuItem {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  bgColor: string;
  badge?: string | number;
  adminOnly?: boolean;
  developerOnly?: boolean;
}

interface Category {
  id: string;
  title: string;
  icon: React.ReactNode;
  items: MenuItem[];
  adminOnly?: boolean;
  developerOnly?: boolean;
}

// TODO: 将来的にfeature flagやDB設定でアンロック判定に切り替え
const isUnlocked = true;

const DashboardV3: React.FC = () => {
  const navigate = useNavigate();
  const { mode, toggleMode, isEventMode } = useOperationMode();
  const { isAdmin, isDeveloper } = useAuth();
  const [quickStats, setQuickStats] = useState<QuickStat[]>([]);
  const [notifications, setNotifications] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const isDevPreviewEnabled = process.env.NODE_ENV === 'development' || window.location.search.includes('dev=true');

  // リアルタイム時刻更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // データ取得
  useEffect(() => {
    if (isUnlocked) {
      fetchDashboardData();
      const interval = setInterval(fetchDashboardData, 60000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const apiHost = window.location.hostname;

      // 本日の売上データ取得
      try {
        const salesResponse = await apiClient.get('/sales-management/today');
        const bentoResponse = await apiClient.get('/bento-orders/today');

        const salesData = salesResponse.data.summary || {};
        const bentoData = bentoResponse.data || [];

        // クイック統計を設定
        setQuickStats([
          {
            label: '本日の売上',
            value: `¥${(salesData.total || 0).toLocaleString()}`,
            change: `${salesData.count || 0}件`,
            icon: <DollarSign className="w-5 h-5" />,
            color: 'text-green-600'
          },
          {
            label: '現金売上',
            value: `¥${(salesData.cash || 0).toLocaleString()}`,
            icon: <Money className="w-5 h-5" />,
            color: 'text-blue-600'
          },
          {
            label: 'PayPay売上',
            value: `¥${(salesData.paypay || 0).toLocaleString()}`,
            icon: <CreditCard className="w-5 h-5" />,
            color: 'text-purple-600'
          },
          {
            label: '弁当予約',
            value: Array.isArray(bentoData) ? bentoData.length : 0,
            change: '本日分',
            icon: <Utensils className="w-5 h-5" />,
            color: 'text-orange-600'
          }
        ]);

        // 通知数を設定
        const pendingOrders = Array.isArray(bentoData)
          ? bentoData.filter((order: any) =>
              order.status === 'pending' || order.status === 'confirmed'
            ).length
          : 0;
        setNotifications(pendingOrders);

      } catch (error) {
        console.error('Sales data fetch error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // カテゴリ別メニュー構成
  const menuCategories: Category[] = [
    {
      id: 'daily-operations',
      title: '日常業務',
      icon: <Store className="w-5 h-5" />,
      items: [
        {
          id: 'pos',
          title: 'POSレジ',
          subtitle: 'レジ業務・会計処理',
          icon: <ShoppingCart className="w-8 h-8" />,
          path: '/',
          color: 'text-white',
          bgColor: 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
        },
        {
          id: 'attendance',
          title: 'かんたん勤怠',
          subtitle: '出勤・退勤記録',
          icon: <Clock className="w-8 h-8" />,
          path: '/attendance',
          color: 'text-white',
          bgColor: 'bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
        },
        {
          id: 'bento-reservation',
          title: '弁当予約管理',
          subtitle: 'テイクアウト予約確認',
          icon: <Utensils className="w-8 h-8" />,
          path: '/bento-reservation',
          color: 'text-white',
          bgColor: 'bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
          badge: notifications > 0 ? notifications : undefined
        }
      ]
    },
    {
      id: 'sales-analytics',
      title: '売上・分析',
      icon: <BarChart3 className="w-5 h-5" />,
      adminOnly: true,
      items: [
        {
          id: 'sales-management',
          title: '売上管理',
          subtitle: '本日の売上確認・訂正',
          icon: <Receipt className="w-6 h-6" />,
          path: '/sales-management',
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200',
          adminOnly: true
        },
        {
          id: 'analytics',
          title: 'ダッシュボード',
          subtitle: 'グラフ・レポート',
          icon: <TrendingUp className="w-6 h-6" />,
          path: '/analytics',
          color: 'text-indigo-600',
          bgColor: 'bg-indigo-50 hover:bg-indigo-100 border border-indigo-200',
          adminOnly: true
        },
        {
          id: 'bento-analytics',
          title: '弁当売上分析',
          subtitle: '弁当予約の分析',
          icon: <ChefHat className="w-6 h-6" />,
          path: '/bento-analytics',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50 hover:bg-orange-100 border border-orange-200',
          adminOnly: true
        },
        {
          id: 'ai-prediction',
          title: 'AI予測',
          subtitle: '需要予測・最適化',
          icon: <Activity className="w-6 h-6" />,
          path: '/ai-prediction',
          color: 'text-purple-600',
          bgColor: 'bg-purple-50 hover:bg-purple-100 border border-purple-200',
          adminOnly: true
        },
        {
          id: 'coupon-analytics',
          title: 'クーポン履歴分析',
          subtitle: 'クーポン使用状況',
          icon: <Receipt className="w-6 h-6" />,
          path: '/coupon-analytics',
          color: 'text-pink-600',
          bgColor: 'bg-pink-50 hover:bg-pink-100 border border-pink-200',
          adminOnly: true
        },
        {
          id: 'revenue-analytics',
          title: 'クーポン収益分析',
          subtitle: '収益・効果測定',
          icon: <TrendingUp className="w-6 h-6" />,
          path: '/revenue-analytics',
          color: 'text-rose-600',
          bgColor: 'bg-rose-50 hover:bg-rose-100 border border-rose-200',
          adminOnly: true
        },
        {
          id: 'reports-calendar',
          title: '日報カレンダー',
          subtitle: '日報作成・閲覧',
          icon: <Calendar className="w-6 h-6" />,
          path: '/reports/calendar',
          color: 'text-sky-600',
          bgColor: 'bg-sky-50 hover:bg-sky-100 border border-sky-200',
          adminOnly: true
        }
      ]
    },
    {
      id: 'accounting',
      title: '経理・会計',
      icon: <DollarSign className="w-5 h-5" />,
      adminOnly: true,
      items: [
        {
          id: 'accounting-management',
          title: '会計管理',
          subtitle: '売上・経費の記録',
          icon: <FileText className="w-6 h-6" />,
          path: '/accounting',
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200',
          adminOnly: true
        },
        {
          id: 'expenses',
          title: '経費管理',
          subtitle: '経費入力・確認',
          icon: <CreditCard className="w-6 h-6" />,
          path: '/expenses',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 hover:bg-blue-100 border border-blue-200',
          adminOnly: true
        },
        {
          id: 'cash-book',
          title: '現金出納帳',
          subtitle: '現金の入出金管理',
          icon: <Calculator className="w-6 h-6" />,
          path: '/cash-book',
          color: 'text-green-600',
          bgColor: 'bg-green-50 hover:bg-green-100 border border-green-200',
          adminOnly: true
        }
      ]
    },
    {
      id: 'staff-management',
      title: 'スタッフ管理',
      icon: <Users className="w-5 h-5" />,
      items: [
        {
          id: 'attendance-report',
          title: '勤怠管理',
          subtitle: '勤怠記録・修正',
          icon: <UserCheck className="w-6 h-6" />,
          path: '/attendance-report',
          color: 'text-green-600',
          bgColor: 'bg-green-50 hover:bg-green-100 border border-green-200'
        },
        {
          id: 'shift-calendar',
          title: 'シフトカレンダー',
          subtitle: 'シフト作成・管理',
          icon: <CalendarDays className="w-6 h-6" />,
          path: '/shift-calendar',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
        },
        {
          id: 'payroll',
          title: '給与管理',
          subtitle: '給与計算・明細',
          icon: <Calculator className="w-6 h-6" />,
          path: '/payroll',
          color: 'text-red-600',
          bgColor: 'bg-red-50 hover:bg-red-100 border border-red-200',
          adminOnly: true
        },
        {
          id: 'staff',
          title: 'スタッフ設定',
          subtitle: 'スタッフ情報管理',
          icon: <Users className="w-6 h-6" />,
          path: '/staff',
          color: 'text-indigo-600',
          bgColor: 'bg-indigo-50 hover:bg-indigo-100 border border-indigo-200',
          adminOnly: true
        }
      ]
    },
    {
      id: 'customer-service',
      title: '顧客サービス',
      icon: <MessageSquare className="w-5 h-5" />,
      adminOnly: true,
      items: [
        {
          id: 'line',
          title: 'LINE会員管理',
          subtitle: '会員情報・ポイント',
          icon: <MessageSquare className="w-6 h-6" />,
          path: '/line',
          color: 'text-green-600',
          bgColor: 'bg-green-50 hover:bg-green-100 border border-green-200',
          adminOnly: true
        },
        {
          id: 'line-analytics',
          title: 'LINE分析',
          subtitle: '会員動向分析',
          icon: <BarChart3 className="w-6 h-6" />,
          path: '/line-analytics',
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200',
          adminOnly: true
        },
        {
          id: 'point-shop',
          title: 'ポイントショップ',
          subtitle: 'ポイント交換管理',
          icon: <Gift className="w-6 h-6" />,
          path: '/point-shop-admin',
          color: 'text-purple-600',
          bgColor: 'bg-purple-50 hover:bg-purple-100 border border-purple-200',
          adminOnly: true
        },
        {
          id: 'achievements-master',
          title: '称号マスタ管理',
          subtitle: '称号の作成・編集',
          icon: <Gift className="w-6 h-6" />,
          path: '/achievements/master',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50 hover:bg-yellow-100 border border-yellow-200',
          adminOnly: true
        },
        {
          id: 'achievements-users',
          title: '会員別称号管理',
          subtitle: '会員の称号付与',
          icon: <Users className="w-6 h-6" />,
          path: '/achievements/users',
          color: 'text-amber-600',
          bgColor: 'bg-amber-50 hover:bg-amber-100 border border-amber-200',
          adminOnly: true
        },
      ]
    },
    {
      id: 'settings',
      title: '設定・管理',
      icon: <Settings className="w-5 h-5" />,
      items: [
        {
          id: 'menu',
          title: 'メニュー管理',
          subtitle: '商品・価格設定',
          icon: <Menu className="w-6 h-6" />,
          path: '/menu',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50 hover:bg-gray-100 border border-gray-200',
          adminOnly: true
        },
        {
          id: 'settings',
          title: 'システム設定',
          subtitle: '各種設定',
          icon: <Settings className="w-6 h-6" />,
          path: '/settings',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
        },
        {
          id: 'admin-feature-flags',
          title: 'Admin管理',
          subtitle: '機能フラグ管理',
          icon: <Settings className="w-6 h-6" />,
          path: '/admin/feature-flags',
          color: 'text-red-600',
          bgColor: 'bg-red-50 hover:bg-red-100 border border-red-200',
          developerOnly: true
        },
        {
          id: 'admin-permissions',
          title: '権限項目管理',
          subtitle: '権限設定',
          icon: <Settings className="w-6 h-6" />,
          path: '/admin/permissions',
          color: 'text-purple-600',
          bgColor: 'bg-purple-50 hover:bg-purple-100 border border-purple-200',
          developerOnly: true
        },
        {
          id: 'login-history',
          title: 'ログイン履歴',
          subtitle: 'アクセス記録',
          icon: <History className="w-6 h-6" />,
          path: '/login-history',
          color: 'text-slate-600',
          bgColor: 'bg-slate-50 hover:bg-slate-100 border border-slate-200',
          adminOnly: true
        },
        {
          id: 'manual',
          title: '取扱説明書',
          subtitle: 'システム利用ガイド',
          icon: <FileText className="w-6 h-6" />,
          path: 'https://line.wa-ton.com/manual/',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
        }
      ]
    }
  ];

  // AccordionSidebarMenu と同じ権限制御をカード表示にも適用
  const filterItemsByPermission = (items: MenuItem[]) => {
    return items.filter(item => {
      if (item.developerOnly && !(isDeveloper() || isDevPreviewEnabled)) return false;
      if (item.adminOnly && !isAdmin()) return false;
      return true;
    });
  };

  const visibleMenuCategories = menuCategories
    .map(category => ({
      ...category,
      items: filterItemsByPermission(category.items),
    }))
    .filter(category => {
      if (category.developerOnly && !(isDeveloper() || isDevPreviewEnabled)) return false;
      if (category.adminOnly && !isAdmin()) return false;
      return category.items.length > 0;
    });

  if (!isUnlocked) {
    return (
      <PageContainer>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* プレミアムオプション案内 */}
          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            borderRadius: 16,
            padding: '48px 32px',
            color: '#fff',
            textAlign: 'center',
            marginBottom: 24,
            boxShadow: '0 20px 60px rgba(79, 172, 254, 0.3)'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F3E0;</div>
            <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px 0', letterSpacing: 1 }}>
              プレミアムオプション
            </h1>
            <h2 style={{ fontSize: 20, fontWeight: 400, margin: '0 0 32px 0', opacity: 0.9 }}>
              管理者ホーム画面（ダッシュボード）
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 16,
              maxWidth: 800,
              margin: '0 auto 32px',
              textAlign: 'left'
            }}>
              {[
                { icon: '\u{1F4B0}', title: 'クイック統計', desc: '本日の売上・現金/PayPay・弁当予約数をリアルタイム表示' },
                { icon: '\u{1F4CB}', title: 'カテゴリ別メニュー', desc: '日常業務・売上分析・経理・スタッフ管理を一画面に集約' },
                { icon: '\u{1F514}', title: '通知・アラート', desc: '未処理の弁当予約など重要な通知をバッジ表示' },
                { icon: '\u{1F512}', title: '権限別表示制御', desc: 'スタッフ・管理者・開発者で表示メニューを自動制御' },
              ].map((feature, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: 12,
                  padding: '16px 20px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>{feature.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{feature.title}</div>
                  <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{feature.desc}</div>
                </div>
              ))}
            </div>

            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 8,
              padding: '12px 32px',
              fontSize: 14,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
            }}>
              &#x1F6A7; 開発検討中 &#x2015; ご契約・お問い合わせは開発担当まで
            </div>
          </div>

          {/* セキュリティ注意書き（黄色枠） */}
          <div style={{
            background: '#fffbe6',
            border: '2px solid #faad14',
            borderRadius: 8,
            padding: '16px 24px',
            marginBottom: 16,
            maxWidth: 600
          }}>
            <p style={{ fontWeight: 700, color: '#d48806', marginBottom: 8, fontSize: 14 }}>
              &#x26A0;&#xFE0F; セキュリティに関するお知らせ
            </p>
            <p style={{ color: '#595959', fontSize: 13, margin: 0, lineHeight: 1.8 }}>
              本画面は開発者用管理画面として暫定的に作成されたものです。<br />
              本格運用にはセキュリティ面での大幅なアップデート（<strong>権限別表示制御の強化</strong>、<strong>APIアクセス制御</strong>等）が必要です。<br />
              実装をご希望の場合は開発担当までご相談ください。
            </p>
          </div>

          {/* ぼかしプレビュー */}
          <div style={{ position: 'relative' }}>
            <div style={{
              filter: 'blur(5px)',
              opacity: 0.5,
              pointerEvents: 'none',
              userSelect: 'none',
            }}>
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800">ホーム画面</h1>
                <p className="text-gray-600 mt-1">
                  {format(currentTime, 'yyyy年MM月dd日 (E) HH:mm:ss', { locale: ja })}
                </p>
              </div>

              {/* ダミークイック統計 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {['本日の売上', '現金売上', 'PayPay売上', '弁当予約'].map((label, i) => (
                  <div key={i} className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-600">{label}</p>
                    <p className="text-2xl font-bold mt-1 text-gray-400">--</p>
                  </div>
                ))}
              </div>

              {/* ダミーカテゴリメニュー */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-800 mb-4">日常業務</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {['POSレジ', 'かんたん勤怠', '弁当予約管理'].map((title, i) => (
                    <div key={i} className="p-6 rounded-xl bg-gray-100 border border-gray-200">
                      <h3 className="font-semibold text-lg text-gray-400">{title}</h3>
                      <p className="text-sm text-gray-300 mt-1">--</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-800 mb-4">売上・分析</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {['売上管理', 'ダッシュボード', '弁当売上分析', 'AI予測'].map((title, i) => (
                    <div key={i} className="p-6 rounded-xl bg-gray-100 border border-gray-200">
                      <h3 className="font-semibold text-lg text-gray-400">{title}</h3>
                      <p className="text-sm text-gray-300 mt-1">--</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ロックオーバーレイ */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 10,
            }}>
              <Lock style={{ width: 48, height: 48, color: '#8c8c8c', opacity: 0.6 }} />
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">ホーム画面</h1>
              <p className="text-gray-600 mt-1">
                {format(currentTime, 'yyyy年MM月dd日 (E) HH:mm:ss', { locale: ja })}
              </p>
            </div>
            {notifications > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-lg">
                <Bell className="w-5 h-5 text-orange-600" />
                <span className="text-orange-700 font-medium">
                  {notifications}件の弁当予約があります
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 出張モードボタン（開発者モードでのみ表示） */}
        {isDevPreviewEnabled && (
          <div className={`mb-6 rounded-lg border-2 p-4 transition-all ${
            isEventMode
              ? 'bg-orange-50 border-orange-300'
              : 'bg-gray-50 border-gray-300'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`font-bold text-lg ${
                  isEventMode ? 'text-orange-800' : 'text-gray-800'
                }`}>
                  {isEventMode ? '🚚 出張モード' : '🏠 通常モード'}
                </h3>
                <p className={`text-sm mt-1 ${
                  isEventMode ? 'text-orange-600' : 'text-gray-600'
                }`}>
                  {isEventMode
                    ? 'テイクアウト専用メニュー | プリンター: 出張先'
                    : '全メニュー表示 | プリンター: 店舗'}
                </p>
              </div>
              <button
                onClick={toggleMode}
                className={`px-6 py-3 rounded-lg font-bold text-white transition-all hover:scale-105 ${
                  isEventMode
                    ? 'bg-gray-600 hover:bg-gray-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {isEventMode ? '通常モードに戻る' : '出張モードに切替'}
              </button>
            </div>
          </div>
        )}

        {/* クイック統計 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {quickStats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  {stat.change && (
                    <p className="text-xs text-gray-500 mt-1">{stat.change}</p>
                  )}
                </div>
                <div className={`${stat.color}`}>{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>

        {/* カテゴリ別メニュー */}
        {visibleMenuCategories.map((category) => (
          <div key={category.id} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="text-gray-600">{category.icon}</div>
              <h2 className="text-xl font-bold text-gray-800">{category.title}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {category.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`relative p-6 rounded-xl transition-all duration-200 ${item.bgColor} group`}
                >
                  {item.badge && (
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold animate-pulse">
                      {item.badge}
                    </div>
                  )}
                  <div className={`${item.color} mb-3`}>{item.icon}</div>
                  <h3 className={`font-semibold text-lg ${item.color}`}>
                    {item.title}
                  </h3>
                  <p className="text-sm opacity-75 mt-1">{item.subtitle}</p>
                  <ChevronRight className="w-5 h-5 absolute bottom-4 right-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* フッター */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-center text-sm text-gray-500">
            <p>和とんPOSシステム v3.0</p>
            <button
              onClick={() => {
                localStorage.removeItem('token');
                navigate('/login');
              }}
              className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              ログアウト
            </button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default DashboardV3;
